/// <reference types="trusted-types"/>
import { Injectable, inject } from '@angular/core';

import { Logger } from '../logging/logger.service';

import { addTrustedTypesPolyfill } from './trusted-type-polyfill';

interface TrustedTypePolicyOptions {
    createHTML: (input: string, ...args: any[]) => string;
    createScript: (input: string, ...args: any[]) => string;
    createScriptURL: (input: string, ...args: any[]) => string;
}

/**
 * This service is used as a fallback for not-notiz
 */
@Injectable({ providedIn: 'root' })
export class DefaultTrustedTypesPolicyService {
    #logger = inject(Logger).createCustomLogger('DefaultTrustedTypesPolicyService');

    constructor() {
        addTrustedTypesPolyfill();
    }

    /**
     * @deprecated use create() instead
     */
    start(trustedTypePolicyOptions?: Pick<TrustedTypePolicyOptions, 'createHTML'>): void {
        this.create(trustedTypePolicyOptions);
    }

    create(trustedTypePolicyOptions?: Pick<TrustedTypePolicyOptions, 'createHTML'>): void {
        if (window.trustedTypes?.defaultPolicy) {
            this.#logger.warn('Default policy already exists, skipping creation.');
        } else {
            this.#createDefaultTrustedTypesPolicy(trustedTypePolicyOptions);
        }
    }

    #createDefaultTrustedTypesPolicy(trustedTypePolicyOptions?: Pick<TrustedTypePolicyOptions, 'createHTML'>): void {
        this.#logger.info(
            'Create default type policy. This should only be used if your spa uses a lib that do not support trusted types'
        );
        const defaultPolicy = this.#extendsTrustedTypesPolicy(trustedTypePolicyOptions);
        // We do not want this exception to bubble up
        try {
            window.trustedTypes?.createPolicy('default', defaultPolicy);
        } catch (exception) {
            this.#logger.error('Unable to create default trusted type policy, please check your CSP settings', {
                exception
            });
        }
    }

    #extendsTrustedTypesPolicy(
        trustedTypePolicyOptions?: Pick<TrustedTypePolicyOptions, 'createHTML'>
    ): Pick<TrustedTypePolicyOptions, 'createHTML'> {
        let createHTML = trustedTypePolicyOptions?.createHTML;
        if (!createHTML) {
            this.#logger.warn(
                `No trusted type createHTML policy given, will always return 'hard sanitized html' instead.`
            );
            createHTML = () => 'hard sanitized html';
        }
        return {
            createHTML: (dirty, type, sink) => {
                this.#logger.debug(`CSP trusted types default policy: ${dirty} ${type} ${sink}`);
                return createHTML(dirty);
            }
        };
    }
}
